import { useEffect, useMemo, useState, useCallback } from "react";
import { AppHeader } from "@/components/layout/app-header";
import { Sidebar } from "@/components/layout/sidebar";
import { AuditFilters } from "@/components/audit/audit-filters";
import { AuditTable, DEFAULT_COLUMN_VISIBILITY, ColumnKey } from "@/components/audit/audit-table";
import { useAuditLogStore } from "@/store/audit-logs";

export const AuditLogPage = () => {
    const fetchEntries = useAuditLogStore((state) => state.fetchEntries);
    const filters = useAuditLogStore((state) => state.filters);
    const pageInfo = useAuditLogStore((state) => state.pageInfo);
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        ...DEFAULT_COLUMN_VISIBILITY,
    });

    useEffect(() => {
        const run = async () => {
            await fetchEntries("initial");
        };

        run().catch(() => null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filters.targetId,
        filters.success,
        filters.actorType,
        filters.model,
        filters.action,
        fetchEntries,
    ]);

    const handleToggleColumn = useCallback((key: ColumnKey, value: boolean) => {
        setVisibleColumns((prev) => {
            const visibleCount = Object.values(prev).filter(Boolean).length;
            if (!value && visibleCount <= 1) {
                return prev;
            }
            return {
                ...prev,
                [key]: value,
            };
        });
    }, []);

    const summaryText = useMemo(() => {
        if (!pageInfo) return "Displaying the 15 most recent changes.";
        if (!pageInfo.count) {
            return "No audit entries for the current filters.";
        }
        const total = pageInfo.total ?? pageInfo.count;
        return `${pageInfo.count} entr${pageInfo.count === 1 ? "y" : "ies"} displayed (of ${total})`;
    }, [pageInfo]);

    return (
        <div className="flex min-h-screen bg-background text-foreground">
            <Sidebar />
            <div className="flex min-h-screen flex-1 flex-col bg-muted/30">
                <AppHeader summaryText={summaryText} />
                <main className="mx-auto flex w-[95%] max-w-none flex-col gap-4 px-6 pb-10">
                    <div className="flex flex-col gap-1 pt-6">
                        <p className="text-sm text-muted-foreground">Use the sidebar to refine actors, actions, models, and outcomes.</p>
                    </div>
                    <AuditFilters
                        visibleColumns={visibleColumns}
                        onToggleColumn={handleToggleColumn}
                    />
                    <AuditTable visibleColumns={visibleColumns} />
                </main>
            </div>
        </div>
    );
};

